import {
    BaseMapConfig,
    MapInitOptions,
    MapInitOverrides,
} from 'src/app/routes/map/vulcan-map-adapter/abstractions/map-abstraction.types';

/** Minimum basemap information required by adapter-wide defaults. */
export interface VulcanMapBaseLayerConfig {
    /** Stable id used to select the default basemap. */
    id: string;
    /** Engine-neutral basemap settings applied after map initialization. */
    config: BaseMapConfig;
}

/** Shared defaults used by every newly initialized Vulcan map adapter. */
export interface VulcanMapAdapterDefaultOptions {
    /** Default camera, zoom bounds, behavior, and controls. */
    mapInitOptions: MapInitOptions;
    /** Application basemaps available to the adapter. */
    baseLayers: readonly VulcanMapBaseLayerConfig[];
    /** Basemap applied after initialization. Omit to use the first configured basemap. */
    defaultBaseLayerId?: string;
}

/** Fully merged initialization settings and optional basemap consumed by a concrete adapter. */
export interface ResolvedVulcanMapInitialization {
    /** Required map settings after application defaults and owner overrides are merged. */
    mapInitOptions: MapInitOptions;
    /** Basemap applied after the concrete engine creates its map. */
    defaultBaseMap?: BaseMapConfig;
}

/** Supplies current application defaults whenever an adapter initializes. */
export type VulcanMapAdapterDefaultOptionsProvider = () => VulcanMapAdapterDefaultOptions;

const LIBRARY_DEFAULT_OPTIONS: VulcanMapAdapterDefaultOptions = {
    mapInitOptions: {
        center: {lat: 0, lng: 0},
        zoom: 2,
    },
    baseLayers: [],
};

let defaultOptionsProvider: VulcanMapAdapterDefaultOptionsProvider = () => LIBRARY_DEFAULT_OPTIONS;

/** Registers application defaults without creating or selecting a concrete map adapter. */
export function configureVulcanMapAdapterDefaults(provider: VulcanMapAdapterDefaultOptionsProvider): void {
    defaultOptionsProvider = provider;
}

/** Merges one map owner's explicit values over the current application defaults. */
export function resolveVulcanMapInitialization(overrides: MapInitOverrides = {}): ResolvedVulcanMapInitialization {
    const defaults = defaultOptionsProvider();
    const mapInitOptions = mergeMapInitOptions(defaults.mapInitOptions, overrides);
    const defaultBaseLayer = getDefaultBaseLayer(defaults);

    return {
        mapInitOptions,
        defaultBaseMap: defaultBaseLayer?.config,
    };
}

/** Creates a complete map configuration without mutating the registered application defaults. */
function mergeMapInitOptions(defaults: MapInitOptions, overrides: MapInitOverrides): MapInitOptions {
    return {
        ...defaults,
        ...overrides,
        center: overrides.center ? {...overrides.center} : {...defaults.center},
        resetBounds: overrides.resetBounds
            ? {
                  southWest: {...overrides.resetBounds.southWest},
                  northEast: {...overrides.resetBounds.northEast},
              }
            : defaults.resetBounds
              ? {
                    southWest: {...defaults.resetBounds.southWest},
                    northEast: {...defaults.resetBounds.northEast},
                }
              : undefined,
        behavior: {
            ...defaults.behavior,
            ...overrides.behavior,
        },
        customMapControl: overrides.customMapControl
            ? [...overrides.customMapControl]
            : defaults.customMapControl
              ? [...defaults.customMapControl]
              : undefined,
    };
}

/** Finds the configured default basemap and reports an invalid id clearly. */
function getDefaultBaseLayer(defaults: VulcanMapAdapterDefaultOptions): VulcanMapBaseLayerConfig | undefined {
    if (defaults.baseLayers.length === 0) {
        return undefined;
    }

    if (!defaults.defaultBaseLayerId) {
        return defaults.baseLayers[0];
    }

    const baseLayer = defaults.baseLayers.find((candidate) => candidate.id === defaults.defaultBaseLayerId);
    if (!baseLayer) {
        throw new Error(`Vulcan Map Adapter basemap "${defaults.defaultBaseLayerId}" is not configured.`);
    }

    return baseLayer;
}
